/*
  Senseless — kit add-to-cart (28 Sep 2026).
  A kit is sold as its parts. One click adds the kit's exact variants (its 4 products + the free
  Cosmetics Bag, from the product's senseless.kit_items metafield) in ONE /cart/add.js request, and
  Matrix Bundles' checkout function merges them into one line at the bundle price. Each product's own
  stock is then reduced, instead of a separate kit SKU's. Markup: snippets/senseless-kit-atc.liquid.
*/
import { CartAddEvent } from '@theme/events';

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement ? el.dataset.sectionId : '')
    .filter(Boolean)
    .join(',');

async function addKit(btn) {
  let items = [];
  try { items = JSON.parse(btn.dataset.kitItems || '[]'); } catch (e) { items = []; }
  if (!items.length) return;
  const bundle = btn.dataset.kitBundle || '';

  const qtyInput = btn.dataset.kitQtyFrom ? document.querySelector(btn.dataset.kitQtyFrom) : null;
  const qty = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10) || 1);
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // The free Cosmetics Bag ("g") is one per bundle in Matrix Bundles: each different set in the cart
  // gets its own, but the same set twice gets one and a second bag would be charged (4 Oct 2026, item 33).
  // Add it once, never multiplied, and skip it only when this set's bag is already there: an unmerged
  // bag labelled _mx_gift_for = this bundle, or this bundle's merged line (/cart.js shows only the
  // merged line, _mx_bundle = this bundle, with the bag folded in).
  // An unlabelled kit (no bundle id) keeps the old rule: any bag or bundle line already uses the gift.
  let hasGift = false;
  try {
    const c = await (await fetch(shopRoot + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    const giftIds = new Set(items.filter((i) => i.g).map((i) => Number(i.id)));
    hasGift = (c.items || []).some((l) => {
      const p = l.properties || {};
      if (bundle) return p._mx_bundle === bundle || p._mx_gift_for === bundle;
      return l.product_type === 'Matrix Bundles helper' || p._mx_bundle || giftIds.has(Number(l.variant_id));
    });
  } catch (e) { /* worst case the bag is added and charged; never block the kit */ }

  const payload = {
    items: items
      .filter((i) => !(i.g && hasGift))
      .map((i) => {
        const line = { id: Number(i.id), quantity: i.g ? 1 : (Number(i.q) || 1) * qty };
        // Label each line with the kit's bundle (2 Oct 2026) so checkout merges it under this kit's name;
        // unlabelled, the first bundle in the app's list (Build Your Own Bundle) took the parts.
        if (bundle) line.properties = i.g ? { _mx_gift_for: bundle } : { _mx_for: bundle };
        return line;
      }),
    sections: cartSectionIds(),
  };

  const root = btn.closest('[data-kit-root]');
  const err = root ? root.querySelector('[data-kit-error]') : null;
  if (err) { err.hidden = true; err.textContent = ''; }
  const label = btn.querySelector('[data-kit-label]') || btn;
  const original = label.textContent;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  label.textContent = 'Adding\u2026';

  try {
    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Sorry, this could not be added to your cart.');

    let cart;
    try {
      cart = await (await fetch(shopRoot + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (e) { /* the drawer still refreshes from the returned sections */ }

    btn.dispatchEvent(new CartAddEvent(cart, btn.id || 'ss-kit', {
      source: 'product-form-component',
      itemCount: payload.items.reduce((sum, i) => sum + i.quantity, 0),
      sections: data.sections,
    }));
    label.textContent = 'Added \u2713';
    setTimeout(() => { label.textContent = original; }, 1600);
  } catch (e) {
    label.textContent = original;
    if (err) { err.textContent = e.message; err.hidden = false; }
    else console.error('[senseless-kit]', e);
  } finally {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-kit-add]') : null;
  if (!btn || btn.disabled) return;
  e.preventDefault();
  e.stopPropagation();
  addKit(btn);
}, true);
